import {
    inject, Injectable
} from "@angular/core";
import { LoggingService } from "../../services/logging/logging.service";
import { InvalidTokenException } from "../invalid-token-exception";
import { ExpiredTokenException } from "../expired-token-exception";
import { MissingTokenException } from "../missing-token-exception";
import { NotificationService } from "../../notification/services/notification.service";
import { RedirectCommand } from "@angular/router";
import { MgdRouterService } from "../../application/services/mgd-router.service";
import { ICommonErrorHandlerService } from "../interfaces/common-error-handler";
import { NavigationSource } from "../../application/enums/navigation-source";
import { DEFAULT_EXCEPTION_MESSAGE } from "../constants/exception-constants";
import { COMMON_APP_CONFIG } from "../../application/constants/app-tokens";
import { ForbiddenException } from "../forbidden-exception";
import { INVALID_TOKEN_MESSAGE } from "../../iapi-application/constants/iapi-app-constants";

/**
 * Last-resort and layer-specific error handler used across the entire application.
 *
 * Implements {@link ICommonErrorHandlerService} and is registered as Angular's global
 * {@link ErrorHandler} (`providedIn: 'root'`), so Angular automatically calls
 * {@link CommonErrorHandlerService#handleError} for any uncaught exception that escapes
 * the change-detection cycle.
 *
 * Each method is scoped to a specific application layer so that log severity and
 * return value can be tailored to that layer's recovery story:
 *
 * - **Uncaught errors** — `handleError` (Angular ErrorHandler entry point; logs at error)
 * - **HttpClient errors** — `handleHttpClientError` (logs at debug; upstream interceptors
 *   re-log at error if unhandled)
 * - **App initializer errors** — `handleInitializerError` (logs at error)
 * - **Service errors** — `handleServiceError` (debug for real `Error` instances; error for
 *   unrecognized types; always returns an `Error` for callers to rethrow)
 * - **Component / directive / pipe errors** — `handleComponentError`, `handleDirectiveError`,
 *   `handlePipeError` (always logs at error; returns `void` — callers must not rethrow)
 * - **Router / guard / resolver / redirect errors** — various `handle*Error` methods that log at error and return or navigate
 *
 * @pattern generic-service
 * @providedIn root
 *
 * @example
 * // In a service — rethrow the returned Error:
 * catch (error) {
 *   throw this._app.errorHandler.handleServiceError(error, "Failed to load deals");
 * }
 *
 * @example
 * // In a component — terminal handler, do not rethrow:
 * catch (error) {
 *   this._app.errorHandler.handleComponentError(error, "Deal list failed to render");
 * }
 *
 * @remarks
 * If you add, remove, or change the signature of any public method, you MUST also update
 * `CommonTestHelper.getErrorHandlerSpyObj`; otherwise many tests will break.
 */
@Injectable({
    providedIn: "root",
})
export class CommonErrorHandlerService implements ICommonErrorHandlerService {
    private readonly _mgdRouter = inject(MgdRouterService);
    private readonly _loggingService = inject(LoggingService);
    private readonly _notificationService = inject(NotificationService);
    private readonly _appConfig = inject(COMMON_APP_CONFIG);

    /**
     * Angular's global error handler entry point for uncaught exceptions.
     *
     * Called automatically by Angular when an error escapes the change-detection cycle.
     * Do not call this method directly — use the layer-specific handler methods below instead.
     *
     * Logs the error at **error** level with the label `"Uncaught Error"`. If the
     * `displayUncaughtErrorMessage` flag is set in the app config, a warning notification
     * is also displayed to the user.
     *
     * @param error - The uncaught error thrown by Angular.
     */
    handleError(error: any): void {
        // the error was never properly caught and handled anywhere else
        // so log it as such
        this._loggingService.logError("Uncaught Error", error);
        if (this._appConfig?.displayUncaughtErrorMessage) {
            // Display a message about the uncaught error
            this._notificationService.displayWarningMessage(
                "There was an unexpected error. You may need to reload your browser. Contact support if the issue persists."
            );
        }
    }

    /**
     * Handles errors that originate from HttpClient interceptors.
     *
     * Logs at **debug** level for diagnostics. HttpClient errors are intentionally not
     * logged as errors at this level because downstream interceptors or higher-level
     * artifacts that cannot handle the error will re-log it at error severity. Logging
     * here at debug avoids duplicate error entries in production log aggregators.
     *
     * @param error - The error thrown by or surfaced through an HttpClient interceptor.
     * @param message - Optional label for the log entry. Defaults to `"HttpClient Error"`.
     */
    handleHttpClientError(error: any, message?: string): void {
        this._loggingService.logDebug(message ?? "HttpClient Error", error);
    }

    /**
     * Handles errors that occur inside Angular app initializers.
     *
     * Logs at **error** level with the label `"App Initializer Error"` (or a custom
     * message if provided). App initializer failures are always fatal to the bootstrap
     * sequence, so they are unconditionally logged as errors.
     *
     * @param error - The error thrown by the app initializer.
     * @param message - Optional label for the log entry. Defaults to `"App Initializer Error"`.
     */
    handleInitializerError(error: any, message?: string): void {
        this._loggingService.logError(message ?? "App Initializer Error", error);
    }

    /**
     * Handles errors that originate inside services and returns an `Error` for the caller
     * to rethrow.
     *
     * Log severity depends on whether the value is a genuine `Error` instance:
     *
     * - **`error instanceof Error`** — logs at **debug** level and returns the original error.
     *   The caller is expected to rethrow it; downstream artifacts that cannot handle it
     *   will log it at error severity, avoiding duplicate error log entries.
     * - **Otherwise** — logs at **error** level with the label `"Unrecognized Error Type"`
     *   and returns a plain `new Error(message ?? DEFAULT_EXCEPTION_MESSAGE)` so callers
     *   always receive a typed value they can rethrow.
     *
     * @param error - The error value caught in the service.
     * @param message - Optional label used both for the log entry and as the fallback
     * message on the returned `Error` when the original value is not an `Error` instance.
     * Defaults to `"Service Error"`.
     * @returns The original `error` when it is an `Error` instance, or a new `Error`
     * constructed from `message` (or `DEFAULT_EXCEPTION_MESSAGE`) otherwise.
     */
    handleServiceError(error: any, message?: string): Error {
        const logMessage = message ?? "Service Error";
        if (error instanceof Error) {
            this._loggingService.logDebug(logMessage, error);
            return error;
        }
        else {
            this._loggingService.logError("Unrecognized Error Type", logMessage, error);
            return new Error(message ?? DEFAULT_EXCEPTION_MESSAGE);
        }
    }

    /**
     * Terminal error handler for errors that originate inside components.
     *
     * Always logs at **error** level — this is a dead-end handler with no return value.
     * Callers that need an `Error` to rethrow should use {@link handleServiceError} instead.
     *
     * Log label depends on whether the value is a genuine `Error` instance:
     *
     * - **`error instanceof Error`** — logs with `message` (or `"Component Error"`) as the label.
     * - **Otherwise** — logs with the label `"Unrecognized Error Type"`.
     *
     * @param error - The error value caught in the component.
     * @param message - Optional label for the log entry. Defaults to `"Component Error"`.
     */
    handleComponentError(error: any, message?: string): void {
        const logMessage = message ?? "Component Error";
        if (error instanceof Error) {
            this._loggingService.logError(logMessage, error);
        }
        else {
            this._loggingService.logError("Unrecognized Error Type", error, new Error(logMessage));
        }
    }

    /**
     * Terminal error handler for errors that originate inside directives.
     *
     * Always logs at **error** level — this is a dead-end handler with no return value.
     * Callers that need an `Error` to rethrow should use {@link handleServiceError} instead.
     *
     * Log label depends on whether the value is a genuine `Error` instance:
     *
     * - **`error instanceof Error`** — logs with `message` (or `"Directive Error"`) as the label.
     * - **Otherwise** — logs with the label `"Unrecognized Error Type"`.
     *
     * @param error - The error value caught in the directive.
     * @param message - Optional label for the log entry. Defaults to `"Directive Error"`.
     */
    handleDirectiveError(error: any, message?: string): void {
        const logMessage = message ?? "Directive Error";
        if (error instanceof Error) {
            this._loggingService.logError(logMessage, error);
        }
        else {
            this._loggingService.logError("Unrecognized Error Type", error, new Error(logMessage));
        }
    }

    /**
     * Terminal error handler for errors that originate inside pipes.
     *
     * Always logs at **error** level — this is a dead-end handler with no return value.
     * Callers that need an `Error` to rethrow should use {@link handleServiceError} instead.
     *
     * Log label depends on whether the value is a genuine `Error` instance:
     *
     * - **`error instanceof Error`** — logs with `message` (or `"Pipe Error"`) as the label.
     * - **Otherwise** — logs with the label `"Unrecognized Error Type"`.
     *
     * @param error - The error value caught in the pipe.
     * @param message - Optional label for the log entry. Defaults to `"Pipe Error"`.
     */
    handlePipeError(error: any, message?: string): void {
        const logMessage = message ?? "Pipe Error";
        if (error instanceof Error) {
            this._loggingService.logError(logMessage, error);
        }
        else {
            this._loggingService.logError("Unrecognized Error Type", error, new Error(logMessage));
        }
    }

    /**
     * Handles errors from route guards.
     *
     * Logs at **error** level, displays an error notification, and returns a
     * {@link RedirectCommand} that tells the router where to navigate. If the route has
     * `errorRedirectPath` configured, the user is sent there; otherwise they are sent to
     * the application's error path.
     *
     * @param error - The error thrown or rejected by the guard.
     * @param message - Optional label for the log entry and notification. Defaults to `"Guard Error"`.
     * @returns A {@link RedirectCommand} for the router to follow.
     */
    handleGuardError(error: any, message?: string): RedirectCommand {
        message = message ?? "Guard Error";
        this._loggingService.logError(message, error);
        this._notificationService.displayErrorMessage(message);
        return this._mgdRouter.getErrorRedirectCommand(message);
    }

    /**
     * Handles errors from redirect functions and returns a {@link RedirectCommand} that
     * tells the router where to navigate.
     *
     * Logs at **error** level and displays an error notification. If the route has
     * `errorRedirectPath` configured, the user is sent there; otherwise they are sent to
     * the application's error path.
     *
     * @param error - The error thrown or rejected by the redirect function.
     * @param message - Optional label for the log entry and notification. Defaults to `"Resolver Error"`.
     * @returns A {@link RedirectCommand} for the router to follow.
     */
    handleRedirectFunctionError(error: any, message?: string): RedirectCommand {
        message = message ?? "Resolver Error";
        this._loggingService.logError(message, error);
        this._notificationService.displayErrorMessage(message);
        return this._mgdRouter.getErrorRedirectCommand(message);
    }

    /**
     * Handles errors from route resolvers and returns a {@link RedirectCommand} that
     * tells the router where to navigate.
     *
     * Logs at **error** level and displays an error notification. If the route has
     * `errorRedirectPath` configured, the user is sent there; otherwise they are sent to
     * the application's error path.
     *
     * @param error - The error thrown or rejected by the resolver.
     * @param message - Optional label for the log entry and notification. Defaults to `"Resolver Error"`.
     * @returns A {@link RedirectCommand} for the router to follow.
     */
    handleResolverError(error: any, message?: string): RedirectCommand {
        message = message ?? "Resolver Error";
        this._loggingService.logError(message, error);
        this._notificationService.displayErrorMessage(message);
        return this._mgdRouter.getErrorRedirectCommand(message);
    }

    /**
     * Called to handle token errors in the Internal API interceptor.
     *
     * Once any relevant errors have been logged the user is redirected to the logout route
     * where the auth service clears the invalid token, displays the error message, and
     * navigates to the login page. This should be the end of the error chain.
     *
     * @param error - The token-related error. Expected to be an {@link InvalidTokenException},
     * {@link ExpiredTokenException}, or {@link MissingTokenException}; any other value is
     * logged as `"Unrecognized Exception in handleTokenError"`.
     */
    handleTokenError(error: any): void {
        // We want to show a user friendly message incase we have an unexpected error message
        let errorMessage = INVALID_TOKEN_MESSAGE;
        if (error instanceof InvalidTokenException) {
            // This means something is seriously wrong with authentication
            this._loggingService.logError("Invalid Token Error", error);
        }
        else if (
            error instanceof ExpiredTokenException
            || error instanceof MissingTokenException
        ) {
            // These errors have user friendly messages
            // We also don't want to log them
            errorMessage = error.message;
        }
        else {
            // An invalid token error provided
            this._loggingService.logError("Unrecognized Exception in handleTokenError", error);
        }

        // By navigating to the logout route the auth service will handle
        // clearing out the invalid token
        // displaying the error message
        // navigating to the login page
        this._mgdRouter.navigateByUrl(this._mgdRouter.logoutUrl, NavigationSource.Error, {
            errorMessage: errorMessage,
        });
    }

    /**
     * Called to handle forbidden errors in the Internal API interceptor.
     *
     * Once any relevant errors have been logged the user is redirected to the application's
     * error route with a permissions-denied message. This should be the end of the error chain.
     *
     * @param error - The forbidden error. Expected to be a {@link ForbiddenException}; any
     * other value is logged as `"Unrecognized Exception in handleForbiddenError"`.
     */
    handleForbiddenError(error: any): void {
        if (error instanceof ForbiddenException) {
            this._loggingService.logError("Forbidden Error", error);
        }
        else {
            this._loggingService.logError("Unrecognized Exception in handleForbiddenError", error);
        }

        this._mgdRouter.navigateByUrl(this._mgdRouter.errorUrl, NavigationSource.Error, {
            errorMessage: "You do not have adequate permissions to perform that action",
        });
    }

    /**
     * Handles an error that occurs when a component is loaded with invalid router state.
     *
     * These errors only arise when a route is misconfigured or a component is not properly
     * implemented. Logs at **error** level, displays an error notification, and navigates
     * the user to the application's error path.
     *
     * Add a `return` statement immediately after this call to halt further execution in
     * the calling function.
     *
     * @param error - The router-state error (typically a `RouterStateException`).
     * @param message - Optional label for the log entry and notification. Defaults to
     * `"Component Router State Error"`.
     *
     * @example
     * // Navigates the user away from this page with an appropriate message
     * this._app.errorHandler.handleComponentRouterStateError(
     *   new RouterStateException("Edit Campaign form was loaded without a selected campaign"));
     * // Stop execution of function
     * return;
     */
    handleComponentRouterStateError(error: any, message?: string): void {
        message = message ?? "Component Router State Error";
        this._loggingService.logError(message, error);
        this._notificationService.displayErrorMessage(message);
        this._mgdRouter.navigateByUrl(this._mgdRouter.errorUrl, NavigationSource.Error, {
            errorMessage: message,
        });
    }
}
